import { useCallback, useMemo, useRef, useState } from "react";
import { useWebSocketContext } from "../context/WebSocketContext";
import { AgentRequest, AgentMessage, WebSocketMessage } from "../types";
import { generateSessionId } from "../utils/websocketUtils";

export const useAgentV2WebSocket = () => {
    const { state, sendMessage, clearMessages, isReady } = useWebSocketContext();
    const [isProcessing, setIsProcessing] = useState(false);
    const [currentRequest, setCurrentRequest] = useState<AgentRequest | null>(null);
    const messages = state.messages as WebSocketMessage<AgentMessage>[];
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
    const sessionId = currentSessionId || generateSessionId(currentRequest?.clientId || 0);
    // Store extra request context so sendClarificationAnswer can re-use it without re-derivation
    const requestContextRef = useRef<{ pageTitle?: string; pagePrompt?: string; tabPrompt?: string } | null>(null);

    const agentMessages = useMemo(() => {
        return messages.filter((msg: WebSocketMessage<AgentMessage>) => msg.action === "agent-create-widget" && msg.data?.sessionId === sessionId);
    }, [messages, sessionId]);

    const progressMessages = useMemo(() => {
        return agentMessages.filter(
            (msg: WebSocketMessage<AgentMessage>) => msg.data?.type === "in-progress" && msg.data?.agentMessage,
        ) as unknown as WebSocketMessage<AgentMessage>[];
    }, [agentMessages]);

    const generateSession = useCallback((clientId: number) => {
        return generateSessionId(clientId);
    }, []);

    const clearSessionMessages = useCallback(() => {
        clearMessages((message) => message.action === "agent-create-widget" && message.data?.sessionId === sessionId);
        setIsProcessing(false);
    }, [clearMessages, sessionId]);

    // Clears current session messages AND rotates the session ID so that any
    // late-arriving server responses for the abandoned session stop matching agentMessages.
    const resetSession = useCallback(() => {
        clearMessages((message) => message.action === "agent-create-widget" && message.data?.sessionId === sessionId);
        setIsProcessing(false);
        setCurrentSessionId(null);
    }, [clearMessages, sessionId]);

    const sendRequest = useCallback(
        (
            prompt: string,
            clientId: number,
            dashboardId: number,
            pageId: number,
            tag?: string,
            userId?: string,
            publishedToAll?: boolean,
            pageTitle?: string,
            pagePrompt?: string,
            tabPrompt?: string,
            conversationHistory?: { prompt: string; widgetSummary: string; widgetSpec?: any }[],
            _dashboardDateRange?: { from: string; to: string },
        ) => {
            const newSessionId = generateSession(clientId);
            setCurrentSessionId(newSessionId);

            const request: AgentRequest = {
                prompt,
                clientId,
                dashboardId,
                pageId,
                tag,
                sessionId: newSessionId,
                userId,
                publishedToAll,
            };

            requestContextRef.current = { pageTitle, pagePrompt, tabPrompt };
            setCurrentRequest(request);
            setIsProcessing(true);

            sendMessage(
                JSON.stringify({
                    action: "agent-v2",
                    timestamp: Date.now(),
                    ...request,
                    pageTitle,
                    pagePrompt,
                    tabPrompt,
                    ...(conversationHistory?.length ? { conversationHistory } : {}),
                }),
            );
        },
        [sendMessage, generateSession],
    );

    // Resumes a paused graph using the same sessionId — does NOT generate a new session.
    // Used after the backend emits a clarification interrupt.
    const sendClarificationAnswer = useCallback(
        (
            answer:
                | { title: string; values: string[] }
                | { title: string; action: "accept" | "reject"; filters?: unknown[] },
        ) => {
            if (!currentSessionId || !currentRequest) return;
            setIsProcessing(true);
            sendMessage(
                JSON.stringify({
                    action: "agent-v2",
                    timestamp: Date.now(),
                    sessionId: currentSessionId,
                    prompt: currentRequest.prompt,
                    clientId: currentRequest.clientId,
                    dashboardId: currentRequest.dashboardId,
                    pageId: currentRequest.pageId,
                    tag: currentRequest.tag,
                    userId: currentRequest.userId,
                    publishedToAll: currentRequest.publishedToAll,
                    ...(requestContextRef.current ?? {}),
                    clarificationAnswer: answer,
                }),
            );
        },
        [sendMessage, currentSessionId, currentRequest],
    );

    const resetProcessing = useCallback(() => setIsProcessing(false), []);

    const confirmPreview = useCallback(
        (pendingWidgetSpec: any, clientId: number, dashboardId: number, pageId: number, tag?: string, userId?: string, publishedToAll?: boolean) => {
            setIsProcessing(true);
            sendMessage(
                JSON.stringify({
                    action: "agent-v2",
                    timestamp: Date.now(),
                    confirmExamplePreview: true,
                    pendingWidgetSpec,
                    sessionId: currentSessionId,
                    clientId,
                    dashboardId,
                    pageId,
                    tag,
                    userId,
                    publishedToAll,
                }),
            );
        },
        [sendMessage, currentSessionId],
    );

    return {
        isReady,
        isProcessing,
        error: state.error,
        messages: agentMessages,
        progressMessages,
        currentRequest,
        sendRequest,
        sendClarificationAnswer,
        confirmPreview,
        clearSessionMessages,
        resetSession,
        resetProcessing,
        generateSession,
    };
};
